import * as React from 'react';
import { gql, useMutation } from '@apollo/client';
import { faWifi } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Heading,
  Icon,
  Surface,
  Text,
  getIcon,
} from '@quality24/design-system';
import {
  InformationAccessMetric,
  InformationGroup,
  UUIDv4,
} from '@quality24/inpatient-typings';
import moment from 'moment';

import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';
import OrderButton from '@/inpatient-patient-pwa/organisms/OrderButton';
import { getRoomId } from '@/inpatient-patient-pwa/services/storage';
import useCustomData from '@/inpatient-patient-pwa/hooks/useCustomData';
import useI18nResolvers from '@/inpatient-patient-pwa/i18n/useI18nResolvers';
import useUserParams from '@/inpatient-patient-pwa/hooks/useUserParams';
import ContentModalTrigger from './ContentModalTrigger';
import { HospitalInformation } from './types';

import styles from './HospitalInformationContent.module.scss';

export const CREATE_INFO_METRIC_MUTATION = gql`
  mutation createInfoMetricMutation(
    $input: CreateInformationAccessMetricInput!
  ) {
    createInformationAccessMetric(input: $input) {
      informationAccessMetric {
        id
      }
    }
  }
`;

export const UPDATE_INFO_METRIC_MUTATION = gql`
  mutation updateInfoMetricMutation($id: UUID!, $closedAt: Datetime!) {
    updateInformationAccessMetricById(
      input: { patch: { closedAt: $closedAt }, id: $id }
    ) {
      informationAccessMetric {
        id
      }
    }
  }
`;

export interface Props {
  information: HospitalInformation[];
  /** The scoped information_group, if any. */
  informationGroup?: Pick<
    InformationGroup,
    'id' | 'nameI18N' | 'descriptionI18N'
  >;
  wifiLogin?: string | null;
  wifiPassword?: string | null;
}

interface CreateInfoMetricResult {
  createInformationAccessMetric: {
    informationAccessMetric: Pick<InformationAccessMetric, 'id'>;
  };
}

interface UpdateInfoMetricResult {
  updateInformationAccessMetric: {
    informationAccessMetric: Pick<InformationAccessMetric, 'id'>;
  };
}

/**
 * Renders a button label, appending the description (when present) right below
 * it with a regular font-weight.
 */
const renderButtonLabel = (
  label: React.ReactNode,
  description?: string,
): React.ReactNode => {
  if (!description) return label;
  return (
    <span className={styles.buttonLabel}>
      <span>{label}</span>
      <span className={styles.buttonDescription}>{description}</span>
    </span>
  );
};

const HospitalInformationContent: React.FunctionComponent<Props> = ({
  information,
  informationGroup,
  wifiLogin = '',
  wifiPassword = '',
}) => {
  const { t } = useTranslation();
  const { resolveI18n } = useI18nResolvers();
  const { generalInfo } = useCustomData();
  const { user } = useAuth();

  const [informationAccessMetricId, setInformationAccessMetricId] =
    React.useState<string | undefined | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userParams = useUserParams();

  const parentId = searchParams.get('parent_id');

  const parentItem = React.useMemo(
    () => (parentId ? information.find((i) => i.id === parentId) : undefined),
    [information, parentId],
  );

  const [createInfoMetricMutation] = useMutation<CreateInfoMetricResult>(
    CREATE_INFO_METRIC_MUTATION,
  );

  const [updateInfoMetricMutation] = useMutation<UpdateInfoMetricResult>(
    UPDATE_INFO_METRIC_MUTATION,
  );

  const createInfoMetric = React.useCallback(
    async (informationSectionId: string) => {
      const result = await createInfoMetricMutation({
        variables: {
          input: {
            informationAccessMetric: {
              roomId: getRoomId(),
              admissionId: user.admissionId,
              informationSectionId,
              openedAt: moment().toISOString(),
            },
          },
        },
      });
      setInformationAccessMetricId(
        result.data?.createInformationAccessMetric.informationAccessMetric.id,
      );
    },
    [createInfoMetricMutation, user.admissionId],
  );

  const updateInfoMetric = React.useCallback(async () => {
    if (informationAccessMetricId) {
      await updateInfoMetricMutation({
        variables: {
          id: informationAccessMetricId,
          closedAt: moment().toISOString(),
        },
      });
    }
  }, [informationAccessMetricId, updateInfoMetricMutation]);

  /**
   * Generate component according to type
   */
  const generateComponent = (info: HospitalInformation): JSX.Element | null => {
    // `data.descriptionI18N` is nested in a JSONB column, so it arrives as a raw
    // locale map — unlike the top-level `*I18N` fields, resolved by the API.
    const description = resolveI18n(info.data?.descriptionI18N) ?? undefined;

    if (info.children && info.children.length > 0) {
      return (
        <Button
          key={info.id}
          className={styles.button}
          size="lg"
          icon={info.data?.icon ? getIcon(info.data.icon) : undefined}
          onClick={() => navigate(`?parent_id=${info.id}`)}
          fluid
        >
          {renderButtonLabel(
            info.buttonLabelI18N || t('hospitalInfo.additionalInfo'),
            description,
          )}
        </Button>
      );
    }

    if (info.type === 'WIFI_SECTION') {
      return (
        <div key={info.id} className="d-flex flex-column gap-2">
          <Heading as="h4">
            <Icon icon={faWifi} size="sm" /> Wi-fi
          </Heading>
          <Surface padding="sm">
            <Text>
              Usuário: <strong>{wifiLogin ?? ''}</strong>
            </Text>
          </Surface>
          <Surface padding="sm">
            <Text>
              Senha: <strong>{wifiPassword ?? ''}</strong>
            </Text>
          </Surface>
        </div>
      );
    }

    if (info.type === 'SECTION_TITLE') {
      return (
        <Heading as="h4" key={info.id} onClick={(e) => e.stopPropagation()}>
          {info.titleI18N}
        </Heading>
      );
    }

    if (info.type === 'CONTENT_BUTTON') {
      return (
        <ContentModalTrigger
          key={info.id}
          content={info}
          createInfoMetric={createInfoMetric}
          updateInfoMetric={updateInfoMetric}
        />
      );
    }

    if (info.type === 'SERVICE_BUTTON') {
      const serviceId = info.data.serviceId as UUIDv4 | undefined;
      const serviceGroupItemId = info.data.serviceGroupItemId as
        | UUIDv4
        | undefined;
      if (!serviceId && !serviceGroupItemId) return null;
      return (
        <OrderButton
          key={info.id}
          variant="inline"
          className={styles.button}
          serviceGroupItemId={serviceGroupItemId}
          serviceId={serviceId}
          icon={info.data.icon ?? info.data.buttonIcon ?? 'faBell'}
          label={info.buttonLabelI18N || '-'}
          onTriggered={() => navigate('/calls')}
        />
      );
    }

    if (info.type === 'LINK_BUTTON') {
      try {
        // The URL constructor will throw an exception if the passed URL is not valid
        const url = new URL(info.data?.url || '');

        if ((info.data?.userParams?.length || 0) > 0) {
          info.data?.userParams?.forEach((param) => {
            const userParam = userParams[param.name];
            if (userParam) {
              url.searchParams.append(param.alias || param.name, userParam);
            }
          });
        }

        return (
          <Button
            className={styles.button}
            icon={info.data?.icon ? getIcon(info.data.icon) : undefined}
            key={info.id}
            onClick={() => {
              if (info.trackable) {
                createInfoMetric(info.id);
              }
              window.open(url.toString(), '_blank');
            }}
            size="lg"
            fluid
          >
            {renderButtonLabel(info.buttonLabelI18N, description)}
          </Button>
        );
      } catch {
        // eslint-disable-next-line no-console
        console.error(`Invalid URL ${info.data?.url}`);
        return null;
      }
    }

    return null;
  };

  return (
    <div className="d-flex flex-column gap-4">
      <Heading as="h2" size="lg1" weight="semiBold">
        {parentItem?.titleI18N || informationGroup?.nameI18N || generalInfo}
      </Heading>

      {informationGroup?.descriptionI18N && (
        <Text>{informationGroup.descriptionI18N}</Text>
      )}

      {parentItem?.contentI18N && <Text>{parentItem?.contentI18N}</Text>}

      <div className="d-flex flex-column gap-2 w-100">
        {(parentItem?.children || []).length > 0
          ? parentItem?.children?.map(generateComponent)
          : information.map(generateComponent)}
      </div>
    </div>
  );
};

export default HospitalInformationContent;
